import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

/**
 * The knowledge layer, wherever a number is shown.
 *
 * Four questions, the fourth of which is the one that keeps a coach out of
 * trouble: what this number does *not* tell you. A product that shows an
 * acute:chronic ratio without saying it cannot predict an injury has taught the
 * coach something false, and taught it with more authority than a textbook.
 *
 * Rendered as a disclosure rather than a tooltip: it has to work on a phone at
 * the side of a pitch, and it has to be readable in Arabic, where a tooltip
 * anchored to the wrong side is worse than no tooltip.
 */
export function Explain({ term, className = '' }: { term: string; className?: string }) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const base = `knowledge.term.${term}`;

  // Positioned against the viewport rather than the button's own box: panels and
  // table cells clip their overflow, and an explanation that is half cut off is
  // worse than none. Closes on scroll for the same reason.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = button.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(352, window.innerWidth - 32);
      const left = Math.min(Math.max(16, rect.left), window.innerWidth - width - 16);
      setAt({ top: rect.bottom + 8, left });
    };
    place();
    const close = () => setOpen(false);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  return (
    <span className={`inline-flex ${className}`}>
      {/*
        The mark stays 18px — it sits beside a heading and must not shout — but
        the thing a thumb lands on is 44px. The padding does that and the
        negative margin gives the space back to the layout, so the hit area
        grows and nothing moves. It was 18px square, the smallest control in the
        product, on the one affordance that explains what a number means.
      */}
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${t(`${base}.label`)} — ${t('knowledge.open')}`}
        className="group -m-[0.8125rem] inline-flex size-11 items-center justify-center p-[0.8125rem]"
      >
        <span className="inline-flex size-[1.125rem] items-center justify-center rounded-full border border-line text-[0.6875rem] font-semibold leading-none text-ink-faint transition-colors group-hover:border-ink group-hover:text-ink">
          ?
        </span>
      </button>

      {open && at ? (
        <span
          role="dialog"
          aria-label={t(`${base}.label`)}
          style={{ position: 'fixed', top: at.top, left: at.left, width: 'min(22rem, calc(100vw - 2rem))' }}
          className="z-50 block rounded-(--radius-panel) border border-line bg-surface p-3 text-start shadow-(--shadow-raised)"
        >
          <span className="mb-2 flex items-baseline gap-2">
            <span className="font-condensed text-subhead font-semibold">{t(`${base}.label`)}</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ms-auto text-meta text-ink-faint hover:text-ink"
            >
              {t('knowledge.close')}
            </button>
          </span>
          <Definition base={base} />
        </span>
      ) : null}
    </span>
  );
}

/** The four answers, in the order a coach needs them. */
export function Definition({ base }: { base: string }) {
  const t = useTranslations();
  const parts = [
    ['what', 'knowledge.what'],
    ['why', 'knowledge.why'],
    ['read', 'knowledge.read'],
    ['notConclude', 'knowledge.notConclude'],
  ] as const;

  return (
    <span className="grid gap-2">
      {parts.map(([key, heading]) => (
        <span key={key} className="block">
          <span
            className={`block text-meta ${
              key === 'notConclude' ? 'text-risk' : 'text-ink-faint'
            }`}
          >
            {t(heading)}
          </span>
          <span className="block text-meta text-ink">{t(`${base}.${key}`)}</span>
        </span>
      ))}
    </span>
  );
}
